import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MediGenieLogo } from "../components/MediGenieLogo";
import api, { apiErrorMessage } from "../lib/api";
import { toast } from "sonner";

export default function PatientRegister() {
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const widgetRef = useRef(null);
  const [siteKey, setSiteKey] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [step, setStep] = useState("details");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", code: "" });

  useEffect(() => {
    api.get("/auth/patient-registration/config").then(({ data }) => setSiteKey(data.enabled ? data.site_key : ""));
  }, []);
  useEffect(() => {
    if (!siteKey) return;
    const render = () => {
      if (window.grecaptcha && captchaRef.current && widgetRef.current == null)
        widgetRef.current = window.grecaptcha.render(captchaRef.current, { sitekey: siteKey, callback: setCaptchaToken, "expired-callback": () => setCaptchaToken("") });
    };
    if (window.grecaptcha) render();
    else {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true; script.defer = true; script.onload = render;
      document.head.appendChild(script);
    }
  }, [siteKey]);

  const requestCode = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/patient-registration/request-code", { ...form, captcha_token: captchaToken });
      setStep("verify"); toast.success("Verification code sent to your email");
    } catch (error) { toast.error(apiErrorMessage(error, "Registration could not be started")); }
    finally { setBusy(false); }
  };
  const verify = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/patient-registration/verify", { email: form.email, code: form.code });
      toast.success("Patient account created"); navigate("/login");
    } catch (error) { toast.error(apiErrorMessage(error, "Verification failed")); }
    finally { setBusy(false); }
  };
  const field = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
    <div className="w-full max-w-lg mg-card p-7">
      <Link to="/" className="flex justify-center mb-5"><MediGenieLogo size={58} /></Link>
      <h1 className="text-2xl font-extrabold text-mg-navy">Create your patient account</h1>
      <p className="text-sm text-slate-500 mt-1">Register online, verify your email, then sign in securely.</p>
      {step === "details" ? <form onSubmit={requestCode} className="mt-6 space-y-4">
        <label className="block text-sm font-medium">Full name<input className="mg-input mt-1" required {...field("name")} /></label>
        <label className="block text-sm font-medium">Email address<input className="mg-input mt-1" type="email" required {...field("email")} /></label>
        <label className="block text-sm font-medium">Phone number <span className="text-slate-400">(optional)</span><input className="mg-input mt-1" {...field("phone")} /></label>
        <label className="block text-sm font-medium">Password<input className="mg-input mt-1" type="password" minLength="8" required {...field("password")} /></label>
        {siteKey ? <div ref={captchaRef} /> : <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Online registration is temporarily unavailable until reCAPTCHA is configured by MediGenie.</p>}
        <button className="mg-btn-primary w-full justify-center" disabled={busy || !captchaToken}>{busy ? "Sending…" : "Send verification code"}</button>
      </form> : <form onSubmit={verify} className="mt-6 space-y-4">
        <p className="rounded-lg bg-blue-50 p-3 text-sm text-mg-navy">Enter the six-digit code sent to <strong>{form.email}</strong>. It expires in 10 minutes.</p>
        <label className="block text-sm font-medium">Verification code<input className="mg-input mt-1 text-center text-xl tracking-[0.35em]" inputMode="numeric" pattern="[0-9]{6}" maxLength="6" required {...field("code")} /></label>
        <button className="mg-btn-primary w-full justify-center" disabled={busy}>{busy ? "Verifying…" : "Verify and create account"}</button>
      </form>}
      <div className="mt-6 text-center text-sm text-slate-500">Already registered? <Link className="font-semibold text-mg-medical" to="/login">Sign in</Link></div>
    </div>
  </div>;
}
